
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useScheduledCoupons } from "@/hooks/campaigns/queries/useScheduledCoupons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { ScheduledCoupon } from "@/types/scheduled-coupons";
import { Search, Filter, AlertCircle, Phone } from "lucide-react";
import { ErrorDetailsDialog } from "./ErrorDetailsDialog";

interface ScheduledCouponsTableProps {
  campaignId?: string;
}

export const ScheduledCouponsTable: React.FC<ScheduledCouponsTableProps> = ({ campaignId }) => {
  const { data: scheduledCoupons, isLoading, error } = useScheduledCoupons(campaignId);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: "Agendado", variant: "secondary" as const, color: "bg-yellow-100 text-yellow-800" },
      active: { label: "Criado", variant: "default" as const, color: "bg-green-100 text-green-800" },
      used: { label: "Usado", variant: "outline" as const, color: "bg-blue-100 text-blue-800" },
      expired: { label: "Expirado", variant: "destructive" as const, color: "bg-gray-100 text-gray-800" },
      cancelled: { label: "Cancelado", variant: "destructive" as const, color: "bg-orange-100 text-orange-800" },
      reverted: { label: "Revertido", variant: "destructive" as const, color: "bg-purple-100 text-purple-800" },
      error: { label: "Erro", variant: "destructive" as const, color: "bg-red-100 text-red-800" }
    };

    const config = statusMap[status as keyof typeof statusMap] || { label: status, variant: "outline" as const, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {status === 'error' && <AlertCircle className="h-3 w-3 mr-1" />}
        {config.label}
      </span>
    );
  };

  const formatCouponValue = (coupon: ScheduledCoupon) => {
    // Mostrar valor se já foi calculado, independente do status
    if (coupon.coupon_value && coupon.coupon_value > 0) {
      return `R$ ${coupon.coupon_value.toFixed(2)}`;
    }
    
    // Apenas mostrar "A calcular" se realmente não há valor calculado
    if (coupon.status === 'pending' && (!coupon.coupon_value || coupon.coupon_value === 0)) {
      return <span className="text-gray-400 italic">A calcular</span>;
    }
    
    return '-';
  };

  const formatMinimumAmount = (coupon: ScheduledCoupon) => {
    if (coupon.minimum_purchase_amount && coupon.minimum_purchase_amount > 0) {
      return `R$ ${coupon.minimum_purchase_amount.toFixed(2)}`;
    }
    
    if (coupon.status === 'pending') {
      return <span className="text-gray-400 italic">A calcular</span>;
    }
    
    return '-';
  };

  const formatCouponCode = (coupon: ScheduledCoupon) => {
    if (coupon.status === 'pending') {
      return <span className="text-gray-400 italic">A gerar</span>;
    }
    return coupon.coupon_code ? (
      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{coupon.coupon_code}</span>
    ) : '-';
  };

  const getCouponSource = (coupon: ScheduledCoupon) => {
    if (coupon.campaign_id && coupon.campaigns) {
      return {
        type: 'campaign',
        name: coupon.campaigns.name,
        details: coupon.campaigns.type
      };
    }
    return {
      type: 'default',
      name: 'Cashback Padrão',
      details: `${coupon.cashback_percentage}% - ${coupon.cashback_multiplier}x`
    };
  };

  // Filtrar cupons baseado na busca, filtro de status e origem
  const filteredCoupons = scheduledCoupons?.filter(coupon => {
    const source = getCouponSource(coupon);
    
    const matchesSearch = searchTerm === "" || 
      coupon.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coupon.customer_name && coupon.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (coupon.customer_phone && coupon.customer_phone.includes(searchTerm)) ||
      (coupon.coupon_code && coupon.coupon_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      source.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || coupon.status === statusFilter;
    
    const matchesSource = sourceFilter === "all" || 
      (sourceFilter === "campaign" && coupon.campaign_id) ||
      (sourceFilter === "default" && !coupon.campaign_id);
    
    return matchesSearch && matchesStatus && matchesSource;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Carregando cashback agendado...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        Erro ao carregar cashback agendado: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Pesquisar por email, nome, telefone, código ou origem..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Agendado</SelectItem>
              <SelectItem value="active">Criado</SelectItem>
              <SelectItem value="used">Usado</SelectItem>
              <SelectItem value="expired">Expirado</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
              <SelectItem value="error">Erro</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Origem" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="campaign">Campanha</SelectItem>
              <SelectItem value="default">Cashback Padrão</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Estatísticas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {['pending', 'active', 'used', 'error', 'cancelled'].map(status => {
          const count = scheduledCoupons?.filter(c => c.status === status).length || 0;
          const labels = {
            pending: 'Agendados',
            active: 'Criados', 
            used: 'Usados',
            error: 'Com Erro',
            cancelled: 'Cancelados'
          };
          const colors = {
            pending: 'bg-yellow-50 text-yellow-700',
            active: 'bg-green-50 text-green-700',
            used: 'bg-blue-50 text-blue-700',
            error: 'bg-red-50 text-red-700',
            cancelled: 'bg-orange-50 text-orange-700'
          };
          return (
            <div key={status} className={`p-3 rounded-lg text-center ${colors[status as keyof typeof colors]}`}>
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-sm">{labels[status as keyof typeof labels]}</div>
            </div>
          );
        })}
        <div className="bg-blue-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-700">
            {scheduledCoupons?.filter(c => !c.campaign_id).length || 0}
          </div>
          <div className="text-sm text-blue-600">Cashback Padrão</div>
        </div>
      </div>

      {/* Tabela */}
      {!filteredCoupons || filteredCoupons.length === 0 ? (
        <div className="text-center p-8 text-gray-500">
          {searchTerm || statusFilter !== "all" || sourceFilter !== "all" ? 
            "Nenhum cashback encontrado com os filtros aplicados" : 
            "Nenhum cashback agendado encontrado"
          }
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Status</TableHead>
                <TableHead>Código</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Valor Mínimo</TableHead>
                <TableHead>Agendado para</TableHead>
                <TableHead>Gasto Total</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCoupons.map((coupon: ScheduledCoupon) => {
                const source = getCouponSource(coupon);
                const hasError = coupon.status === 'error' || coupon.status === 'cancelled';
                
                return (
                  <TableRow key={coupon.id} className="hover:bg-gray-50">
                    <TableCell>
                      {getStatusBadge(coupon.status)}
                    </TableCell>
                    <TableCell>
                      {formatCouponCode(coupon)}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {coupon.customer_name && (
                          <div className="font-medium">{coupon.customer_name}</div>
                        )}
                        <div className={coupon.customer_name ? "text-sm text-gray-500" : "font-medium"}>
                          {coupon.customer_email}
                        </div>
                        {coupon.customer_phone && (
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {coupon.customer_phone}
                          </div>
                        )}
                        <div className="text-sm text-gray-500">ID: {coupon.reference_order_id}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatCouponValue(coupon)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatMinimumAmount(coupon)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {coupon.scheduled_for ? (
                        <div className="text-sm">
                          <div>{format(new Date(coupon.scheduled_for), "dd/MM/yyyy", { locale: ptBR })}</div>
                          <div className="text-gray-500">{format(new Date(coupon.scheduled_for), "HH:mm", { locale: ptBR })}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      R$ {coupon.total_spent_amount?.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium flex items-center gap-2">
                          {source.name}
                          {source.type === 'default' && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                              Padrão
                            </span>
                          )}
                        </div>
                        <div className="text-gray-500">{source.details}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {hasError && (
                        <ErrorDetailsDialog coupon={coupon} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
